import { useCallback, useEffect, useState } from 'react';
import Nav from '../../components/nav';
import Spinner from '../../components/spinner';
import { ConnectPanel } from '../../components/dashboards/ConnectPanel';
import { DashboardList } from '../../components/dashboards/DashboardList';
import { DashboardsHero } from '../../components/dashboards/DashboardsHero';
import { PromptGallery } from '../../components/dashboards/PromptGallery';
import { HowItWorks } from '../../components/dashboards/HowItWorks';
import { listDashboards, seedExample, deleteDashboard } from '../../services/dashboards';
import { fetchDevices } from '../../services/device';

/**
 * The Dashboards surface: a grid when there is anything to list, the lander
 * when there is not.
 *
 * One state value drives it, and it is also what guarantees the seed is
 * attempted at most once per mount: nothing transitions back into the
 * seeding branch except the error screen's explicit retry.
 */
export default function Dashboards() {
  // { kind: 'loading' | 'list' | 'lander' | 'error' }
  // `list` with no items (the example was deleted) renders the lander too.
  const [state, setState] = useState({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    let page;
    try {
      page = await listDashboards();
      if (page.items.length > 0) return setState({ kind: 'list', items: page.items });
    } catch (e) {
      return setState({ kind: 'error', error: e });
    }

    try {
      const record = await seedExample();
      // The example is just the first card; it opens like any other.
      return setState({ kind: 'list', items: [record] });
    } catch (e) {
      if (e && e.status === 409) {
        // Two causes, one status: a second tab seeded while this one looked,
        // or the user deleted the example and will not be handed another.
        // Only the list can tell them apart.
        try {
          const after = await listDashboards();
          return setState({ kind: 'list', items: after.items });
        } catch (e2) {
          return setState({ kind: 'error', error: e2 });
        }
      }

      if (e && e.status === 422) {
        // Nothing has reported yet. The lander shows either way; the device
        // list only decides its second button and the name in the prompt.
        try {
          const devices = await fetchDevices();
          return setState({
            kind: 'lander',
            noDevices: devices.length === 0,
            // Naming an arbitrary one of three would be worse than none.
            deviceName: devices.length === 1 ? devices[0].name : undefined,
          });
        } catch (e2) {
          return setState({ kind: 'error', error: e2 });
        }
      }

      return setState({ kind: 'error', error: e });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (slug) => {
    // Optimistic: the card goes now and comes back if the API disagrees.
    const before = state.items;
    setState({ kind: 'list', items: before.filter((d) => d.id !== slug) });
    try {
      await deleteDashboard(slug);
    } catch (e) {
      setState({ kind: 'list', items: before });
    }
  };

  const shell = (body) => (
    <div className="min-h-screen bg-[#f7f7f5] dark:bg-gray-950">
      <Nav />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">{body}</main>
    </div>
  );

  const header = (count) => (
    <header>
      <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl dark:text-gray-50">Dashboards</h1>
      {count !== undefined && (
        <p className="mt-2 text-lg text-gray-500 dark:text-gray-400">
          {count} {count === 1 ? 'dashboard' : 'dashboards'}
        </p>
      )}
    </header>
  );

  if (state.kind === 'loading') {
    return shell(<div aria-busy="true"><Spinner /></div>);
  }

  if (state.kind === 'error') {
    return shell(
      <>
        {header()}
        <div role="alert" className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
          <p className="text-gray-900 dark:text-gray-50">
            {state.error?.error?.message || state.error?.message ||
              'Dashboards could not be loaded.'}
          </p>
          <button
            type="button"
            onClick={load}
            className="mt-4 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
          >
            Try again
          </button>
        </div>
      </>
    );
  }

  if (state.kind === 'lander' || state.items.length === 0) {
    return shell(
      <div className="space-y-20">
        <DashboardsHero noDevices={state.noDevices} />
        <PromptGallery />
        <div className="space-y-6">
          <HowItWorks />
          <ConnectPanel deviceName={state.deviceName} defaultOpen />
        </div>
      </div>
    );
  }

  return shell(
    <>
      {header(state.items.length)}
      <div className="mt-8">
        <DashboardList items={state.items} onDelete={onDelete} />
      </div>
      <div className="mt-10">
        <ConnectPanel />
      </div>
    </>
  );
}
